import 'react-day-picker/dist/style.css';

import { CSSProperties, FC } from 'react';
import { DayPicker, DayPickerSingleProps } from 'react-day-picker';
import { Box, Center } from '@chakra-ui/react';

import Button from '../Button';
import Caption from './components/Caption';
import PeriodPicker from './components/PeriodPicker';
import locales from './locales';

const cellStyle: CSSProperties = {
  fontSize: 'font-13',
  fontWeight: 'weight-400',
  height: '24px',
  width: '34px',
};

export type DatePickerViewMode = 'day' | 'month' | 'year' | 'quarter';

export type DatePickerProps = {
  onChange: (date: Date | undefined) => void;
  value: DayPickerSingleProps['selected'];
  modifiers?: DayPickerSingleProps['modifiers'];
  viewMode?: DatePickerViewMode;
} & (
  | {
      withSetTodayButton?: never;
      initialFocus?: never;
    }
  | {
      withSetTodayButton?: boolean;
      initialFocus?: DayPickerSingleProps['initialFocus'];
    }
);

const WIDTH = '280px';

const DatePicker: FC<DatePickerProps> = ({
  modifiers,
  onChange,
  value,
  viewMode = 'day',
  withSetTodayButton = true,
  ...props
}) => {
  if (viewMode && viewMode !== 'day') {
    return (
      <PeriodPicker
        maxWidth={WIDTH}
        minWidth={WIDTH}
        modifiers={modifiers}
        onChange={onChange}
        padding="space-16"
        value={value}
        viewMode={viewMode}
      />
    );
  }

  return (
    <Box
      padding="space-16"
      sx={{
        // main container
        '.rdp': {
          margin: 0,
        },

        // day hover state
        '.rdp-button:hover:not([disabled]):not(.rdp-day_selected)': {
          backgroundColor: 'grey.200',
        },

        // reset default box size
        '.rdp-cell, .rdp-head_cell': {
          height: 'inherit',
          width: 'inherit',
        },

        // day
        '.rdp-day': {
          ...cellStyle,
          borderRadius: '4px',
          color: 'primary',
          transition: 'all 0.2s ease-out',
        },

        // focus-within on days
        '.rdp-day:focus-visible:not([disabled])': {
          backgroundColor: 'white',
          border: 0,
          boxShadow: 'outline-rebranded',
          outline: 'none',
        },

        // displayed with props.showOutsideDays
        '.rdp-day_outside': {
          color: 'grey.500',
        },

        // selected day
        '.rdp-day_selected, .rdp-day_selected:focus-visible:not([disabled])': {
          backgroundColor: 'primary',
          color: 'white',
          fontWeight: 'weight-700',
        },

        // current day
        '.rdp-day_today': {
          fontWeight: 'weight-500',
        },

        // heading cells (days of the week)
        '.rdp-head_cell': {
          ...cellStyle,
          color: 'secondary',
          fontWeight: 'weight-500',
          paddingY: 'space-16',
        },

        // remove padding-bottom from last row
        '.rdp-row:not(:last-of-type) .rdp-cell': {
          paddingBottom: 'space-8',
        },

        // table wrapper
        '.rdp-table': {
          borderCollapse: 'separate',
          borderSpacing: '2px',
        },
      }}
      width={WIDTH}
    >
      <DayPicker
        components={{
          Caption: (captionProps) => <Caption {...captionProps} />,
        }}
        locale={locales.dateFnsLocale}
        mode="single"
        modifiers={modifiers}
        onSelect={(date) => onChange(date)}
        selected={value}
        showOutsideDays
        {...props}
      />
      {withSetTodayButton ? (
        <Center paddingY="space-16">
          <Button onClick={() => onChange?.(new Date())} variant="discreet">
            {locales.today}
          </Button>
        </Center>
      ) : null}
    </Box>
  );
};

export default DatePicker;
